// ─── Beach Cave Map ───────────────────────────────────────────────────────────
// A dark, narrow cave accessed from the Town Center south exit.
// Rare evolved species lurk here at levels 12-20. No trainers, no NPCs —
// deeper-exploration zone for players seeking power-spikes and loot.
//
// TODO: Once the canonical `GameMap` interface lands in `src/engine/world-map.ts`,
//       replace this `as const` literal with an `export const BEACH_CAVE_MAP: GameMap = ...`.
//       The current `WorldMap` class in `engine/world-map.ts` is a procedural overworld
//       generator (returns RoomData), not a static map data shape, so this file mirrors
//       the data-driven shape used by sibling map agents (Route 1, Town Center).
//
// Tile legend (numeric for compactness; matches the small-integer convention
// used by `engine/constants.ts` TileType):
//   0 = WALL    (blocked, dark stone)
//   1 = FLOOR   (walkable cave floor)
//   2 = EXIT    (south edge — back to town_center)
//   3 = TREASURE (walkable pickup tile)

const W = 0; // wall
const F = 1; // floor
const E = 2; // exit
const T = 3; // treasure

// 16 wide × 20 tall. Narrow winding corridors — roughly 30% walkable.
// Visual layout (top of array = north / back of cave; bottom = south / exit):
const tiles: readonly (readonly number[])[] = [
  [W, W, W, W, W, W, W, W, W, W, W, W, W, W, W, W], // row 0   — back wall
  [W, F, F, F, W, W, W, W, W, W, W, W, T, F, F, W], // row 1   — treasure alcove (top-right)
  [W, F, W, F, F, F, W, W, W, W, W, F, F, F, W, W], // row 2
  [W, F, W, W, W, F, F, W, W, W, F, F, W, W, W, W], // row 3
  [W, F, F, W, W, W, F, F, F, F, F, W, W, W, W, W], // row 4
  [W, W, F, W, W, W, W, W, W, W, F, W, W, F, F, W], // row 5
  [W, W, F, F, F, W, W, W, W, W, F, F, F, F, W, W], // row 6
  [W, W, W, W, F, F, F, W, W, W, W, W, W, F, W, W], // row 7
  [W, F, F, W, W, W, F, F, F, W, W, F, F, F, W, W], // row 8   — central junction
  [W, F, W, W, W, W, W, W, F, F, W, F, W, W, W, W], // row 9
  [W, F, F, F, F, W, W, W, W, F, F, F, W, W, W, W], // row 10
  [W, W, W, W, F, F, W, W, W, W, W, F, F, F, W, W], // row 11
  [W, W, W, W, W, F, F, W, W, W, W, W, W, F, W, W], // row 12
  [W, F, F, F, W, W, F, F, W, W, F, F, F, F, W, W], // row 13
  [W, F, W, F, F, W, W, F, F, F, F, W, W, W, W, W], // row 14
  [W, F, W, W, F, F, W, W, W, W, W, W, W, W, W, W], // row 15
  [W, F, F, W, W, F, F, F, F, W, W, W, W, W, W, W], // row 16
  [W, W, F, F, W, W, W, W, F, F, F, F, W, W, W, W], // row 17
  [W, W, W, F, F, F, F, F, F, W, W, F, F, F, W, W], // row 18 — funnel toward exit
  [W, W, W, W, W, W, W, E, E, W, W, W, W, W, W, W], // row 19 — south exit to town_center
] as const;

export const BEACH_CAVE_MAP = {
  id: "beach_cave",
  name: "Beach Cave",
  width: 16,
  height: 20,
  tiles,

  // One encounter zone covering the full interior. The map's `tiles` array
  // gates which positions are actually walkable; the engine should only roll
  // encounters on FLOOR (1) tiles inside the bounds.
  encounterZones: [
    {
      id: "beach_cave_depths",
      bounds: { x: 1, y: 1, w: 14, h: 18 },
      // Rare pool — evolved forms only, with a sliver of legendary chance.
      // Species ids are stringified `SPECIES_DEX` numeric ids.
      //   "4"  Debuggor   (evolved tech)
      //   "10" Sysmonk    (evolved ops)
      //   "6"  Stonkzilla (evolved finance)
      //   "11" Cratus     (legendary — catchRate 3)
      speciesPool: [
        { speciesId: "4",  weight: 40, levelMin: 12, levelMax: 18 }, // Debuggor
        { speciesId: "10", weight: 35, levelMin: 14, levelMax: 20 }, // Sysmonk
        { speciesId: "6",  weight: 23, levelMin: 13, levelMax: 19 }, // Stonkzilla
        { speciesId: "11", weight: 2,  levelMin: 18, levelMax: 20 }, // Cratus (legendary)
      ],
    },
  ],

  // South edge exit — drops the player back into Town Center at its north entrance.
  // TODO: align targetX / targetY with Town Center map's actual north-entrance coord
  //       once that map's exports are finalized.
  exits: [
    { x: 7, y: 19, targetMapId: "town_center", targetX: 10, targetY: 1 },
    { x: 8, y: 19, targetMapId: "town_center", targetX: 11, targetY: 1 },
  ],

  // No NPCs / trainers in the cave — deep exploration only.
  trainers: [],

  // Single rare pickup tucked in the top-right alcove.
  pickups: [
    { x: 12, y: 1, itemId: "rare_candy_1", consumed: false },
  ],
} as const;
